/** Settings overview + sub-pages. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { StickySaveBar } from "@/components/common/StickySaveBar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";

import { PriceInput } from "@/components/shared/PriceInput";
import { BitrixSettings } from "@/components/settings/BitrixSettings";
import { AnthropicSettings } from "@/components/settings/AnthropicSettings";
import { GoogleTranslateSettings } from "@/components/settings/GoogleTranslateSettings";
import { YandexMapsSettings } from "@/components/settings/YandexMapsSettings";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<any>({});

  const { data: settings, isLoading } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: async () => {
      const { data } = await api.get("/settings");
      return data;
    },
  });

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async (patch: any) => {
      await api.patch("/settings", patch);
    },
    onSuccess: () => {
      toast.success(t("common.toast.saved"));
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
    },
  });

  const { data: deliveryMethods } = useQuery({
    queryKey: ["admin-delivery-methods"],
    queryFn: async () => (await api.get("/delivery-methods")).data,
  });
  const { data: paymentMethods } = useQuery({
    queryKey: ["admin-payment-methods"],
    queryFn: async () => (await api.get("/payment-methods")).data,
  });

  if (isLoading || !settings) return <Skeleton className="h-96" />;

  function update(field: string, value: any) {
    setForm({ ...form, [field]: value });
  }

  const generalAndBonusFields = [
    "contact_phone",
    "contact_email",
    "whatsapp_number",
    "telegram_link",
    "instagram_link",
    "address",
    "working_hours",
    "bonus_earn_percent",
    "bonus_max_spend_percent",
    "bonus_expire_days",
    "bonus_min_order_to_earn",
  ];
  const dirty = generalAndBonusFields.some(
    (k) => (form[k] ?? null) !== (settings[k] ?? null),
  );

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-heading">Настройки</h1>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">Общие</TabsTrigger>
          <TabsTrigger value="bonuses">Бонусы</TabsTrigger>
          <TabsTrigger value="delivery">Доставка</TabsTrigger>
          <TabsTrigger value="payment">Оплата</TabsTrigger>
          <TabsTrigger value="bitrix">Bitrix24</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Контакты</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label>Телефон</Label>
                <Input
                  value={form.contact_phone ?? ""}
                  onChange={(e) => update("contact_phone", e.target.value)}
                  placeholder="+998 71 200 70 70"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.contact_email ?? ""}
                  onChange={(e) => update("contact_email", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>WhatsApp number (для кнопки в mobile)</Label>
                <Input
                  value={form.whatsapp_number ?? ""}
                  onChange={(e) => update("whatsapp_number", e.target.value)}
                  placeholder="998901234567"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Telegram link</Label>
                <Input
                  value={form.telegram_link ?? ""}
                  onChange={(e) => update("telegram_link", e.target.value)}
                  placeholder="https://t.me/bestdigital_uz"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Instagram link</Label>
                <Input
                  value={form.instagram_link ?? ""}
                  onChange={(e) => update("instagram_link", e.target.value)}
                  placeholder="https://instagram.com/bestdigital.uz"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Адрес</Label>
                <Input
                  value={form.address ?? ""}
                  onChange={(e) => update("address", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Рабочие часы</Label>
                <Input
                  value={form.working_hours ?? ""}
                  onChange={(e) => update("working_hours", e.target.value)}
                  placeholder={t("settings.workingHoursPlaceholder")}
                />
              </div>
            </CardContent>
          </Card>

          <GoogleTranslateSettings
            keySet={Boolean(settings.google_translate_api_key_set)}
          />

          <YandexMapsSettings
            keySet={Boolean(settings.yandex_maps_api_key_set)}
          />

          <AnthropicSettings
            keySet={Boolean(settings.anthropic_api_key_set)}
          />
        </TabsContent>

        <TabsContent value="bonuses" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Настройки бонусной программы</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label>% начисления за заказ</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.bonus_earn_percent ?? ""}
                  onChange={(e) =>
                    update("bonus_earn_percent", parseFloat(e.target.value))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Макс % списания на заказ</Label>
                <Input
                  type="number"
                  value={form.bonus_max_spend_percent ?? ""}
                  onChange={(e) =>
                    update("bonus_max_spend_percent", parseInt(e.target.value, 10))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Срок жизни бонусов (дни)</Label>
                <Input
                  type="number"
                  value={form.bonus_expire_days ?? ""}
                  onChange={(e) =>
                    update("bonus_expire_days", parseInt(e.target.value, 10))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Мин. сумма заказа для начисления</Label>
                <PriceInput
                  value={form.bonus_min_order_to_earn}
                  onChange={(v) => update("bonus_min_order_to_earn", v ?? 0)}
                />
              </div>
            </CardContent>
          </Card>

        </TabsContent>

        <TabsContent value="delivery" className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Название (RU)</TableHead>
                <TableHead>Стоимость</TableHead>
                <TableHead>Бесплатно от</TableHead>
                <TableHead>ETA</TableHead>
                <TableHead>Активен</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveryMethods?.map((m: any) => (
                <DeliveryRow key={m.id} method={m} />
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="payment" className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Название (RU)</TableHead>
                <TableHead>Online</TableHead>
                <TableHead>Активен</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paymentMethods?.map((m: any) => (
                <PaymentRow key={m.id} method={m} />
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="bitrix" className="pt-4">
          <BitrixSettings />
        </TabsContent>
      </Tabs>

      <StickySaveBar
        visible={dirty}
        saving={saveMutation.isPending}
        onSave={() => saveMutation.mutate(form)}
        onDiscard={() => setForm(settings)}
        message="Изменения в настройках"
      />
    </div>
  );
}

function DeliveryRow({ method }: { method: any }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: async (patch: any) => {
      await api.patch(`/delivery-methods/${method.id}`, patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-delivery-methods"] });
      toast.success(t("common.toast.saved"));
    },
  });
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">{method.code}</TableCell>
      <TableCell>
        <Input
          defaultValue={method.name_ru}
          onBlur={(e) =>
            e.target.value !== method.name_ru &&
            updateMutation.mutate({ name_ru: e.target.value })
          }
        />
      </TableCell>
      <TableCell className="w-44">
        <PriceInput
          value={method.base_cost}
          onChange={(v) =>
            v != null && v !== method.base_cost && updateMutation.mutate({ base_cost: v })
          }
        />
      </TableCell>
      <TableCell className="w-44">
        <PriceInput
          value={method.free_threshold}
          onChange={(v) => updateMutation.mutate({ free_threshold: v })}
        />
      </TableCell>
      <TableCell className="w-20">
        <Input
          type="number"
          defaultValue={method.eta_days ?? ""}
          onBlur={(e) => {
            const n = parseInt(e.target.value, 10);
            if (!isNaN(n) && n !== method.eta_days) {
              updateMutation.mutate({ eta_days: n });
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={method.is_active}
          onCheckedChange={(is_active) => updateMutation.mutate({ is_active })}
        />
      </TableCell>
    </TableRow>
  );
}

function PaymentRow({ method }: { method: any }): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: async (patch: any) => {
      await api.patch(`/payment-methods/${method.id}`, patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payment-methods"] });
      toast.success(t("common.toast.saved"));
    },
  });
  return (
    <TableRow>
      <TableCell className="font-mono text-xs">{method.code}</TableCell>
      <TableCell>
        <Input
          defaultValue={method.name_ru}
          onBlur={(e) =>
            e.target.value !== method.name_ru &&
            updateMutation.mutate({ name_ru: e.target.value })
          }
        />
      </TableCell>
      <TableCell>
        {method.is_online ? "✓" : "—"}
      </TableCell>
      <TableCell>
        <Switch
          checked={method.is_active}
          onCheckedChange={(is_active) => updateMutation.mutate({ is_active })}
        />
      </TableCell>
    </TableRow>
  );
}
